import { useState, useEffect, useRef } from "react";
import { ListenerLayout } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Image, Video, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ListenerProfileEdit() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    bio: "",
    experience: ""
  });
  
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/listener/profile`);
      const data = response.data;
      setProfile(data);
      setFormData({
        username: data.username || "",
        bio: data.bio || "",
        experience: data.experience || ""
      });
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const response = await axios.put(`${API}/listener/profile`, formData);
      setProfile(response.data);
      toast.success("Perfil actualizado");
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error(error.response?.data?.detail || "Error al actualizar el perfil");
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Por favor selecciona una imagen (JPG, PNG o WebP)");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("La imagen es muy grande. Máximo 5MB.");
      return;
    }

    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/listener/upload/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProfile(prev => ({ ...prev, photo_url: response.data.photo_url }));
      toast.success("Foto subida correctamente");
    } catch (error) {
      console.error("Error uploading photo:", error);
      toast.error(error.response?.data?.detail || "Error al subir la foto");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('video/')) {
      toast.error("Por favor selecciona un video (MP4, WebM o MOV)");
      return;
    }

    // Validate file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      toast.error("El video es muy grande. Máximo 100MB.");
      return;
    }

    setUploadingVideo(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/listener/upload/video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProfile(prev => ({ ...prev, video_url: response.data.video_url }));
      toast.success("Video subido correctamente");
    } catch (error) {
      console.error("Error uploading video:", error);
      toast.error(error.response?.data?.detail || "Error al subir el video");
    } finally {
      setUploadingVideo(false);
    }
  };

  const getFullUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  if (loading) {
    return (
      <ListenerLayout title="Mi perfil">
        <div className="flex justify-center py-12">
          <div className="spinner"></div>
        </div>
      </ListenerLayout>
    );
  }

  return (
    <ListenerLayout title="Mi perfil">
      <div className="max-w-2xl space-y-6">
        {/* Photo Upload */}
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Image className="w-5 h-5" />
            Foto de perfil
          </h2>
          
          <div className="flex items-center gap-6">
            <div className="relative">
              {profile?.photo_url ? (
                <img 
                  src={getFullUrl(profile.photo_url)} 
                  alt="Foto de perfil" 
                  className="w-24 h-24 rounded-full object-cover border-4 border-stone-100"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className={`w-24 h-24 rounded-full bg-stone-100 flex items-center justify-center ${profile?.photo_url ? 'hidden' : ''}`}>
                <Image className="w-8 h-8 text-stone-400" />
              </div>
            </div>
            
            <div>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handlePhotoUpload}
                className="hidden"
                data-testid="photo-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => photoInputRef.current?.click()}
                disabled={uploadingPhoto}
                data-testid="upload-photo-btn"
              >
                {uploadingPhoto ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Subiendo...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Subir foto
                  </>
                )}
              </Button>
              <p className="mt-2 text-xs text-stone-500">JPG, PNG o WebP. Máximo 5MB.</p>
            </div>
          </div>
        </div>

        {/* Video Upload */}
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Video className="w-5 h-5" />
            Video de presentación
          </h2>
          
          <div className="space-y-4">
            {profile?.video_url && (
              <div className="rounded-xl overflow-hidden bg-stone-100">
                <video 
                  src={getFullUrl(profile.video_url)} 
                  controls 
                  className="w-full max-h-64 object-contain"
                />
              </div>
            )}
            
            <div>
              <input
                ref={videoInputRef}
                type="file"
                accept="video/mp4,video/webm,video/quicktime"
                onChange={handleVideoUpload}
                className="hidden"
                data-testid="video-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => videoInputRef.current?.click()}
                disabled={uploadingVideo}
                data-testid="upload-video-btn"
              >
                {uploadingVideo ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Subiendo video...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    {profile?.video_url ? "Cambiar video" : "Subir video"}
                  </>
                )}
              </Button>
              <p className="mt-2 text-xs text-stone-500">MP4, WebM o MOV. Máximo 100MB.</p>
            </div>
          </div>
        </div>

        {/* Profile Info */}
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">Información del perfil</h2>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="username">Nombre de usuario (URL)</Label>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-stone-500 text-sm">apoyum.com/l/</span>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '') })}
                  placeholder="tunombre"
                  className="flex-1"
                  data-testid="profile-username"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="bio">Biografía</Label>
              <Textarea
                id="bio"
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                placeholder="Cuéntales a los usuarios sobre ti..."
                rows={4}
                className="mt-2"
                data-testid="profile-bio"
              />
            </div>

            <div>
              <Label htmlFor="experience">Experiencia</Label>
              <Textarea
                id="experience"
                value={formData.experience}
                onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                placeholder="Describe tu experiencia y formación..."
                rows={4}
                className="mt-2"
                data-testid="profile-experience"
              />
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-stone-200">
              <div>
                <span className="text-sm text-stone-500">Estado: </span>
                <span className={`${
                  profile?.status === 'approved' ? 'badge-approved' : 
                  profile?.status === 'pending' ? 'badge-pending' : 'badge-rejected'
                }`}>
                  {profile?.status === 'approved' ? 'Aprobado' : 
                   profile?.status === 'pending' ? 'Pendiente' : 'Rechazado'}
                </span>
              </div>
              <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-profile-btn">
                {saving ? "Guardando..." : "Guardar cambios"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </ListenerLayout>
  );
}
